// Color utilities for getting colors from database
// This replaces all hardcoded color values with dynamic database lookups

export interface StatusData {
  id: string;
  name: string;
  displayName?: string;
  semanticType?: string;
  color: string;
  userId: string;
  createdAt: string;
}

export interface PriorityData {
  id: string;
  name: string;
  color?: string;
  isDefault?: boolean;
  organizationId: string;
  userId?: string;
  createdAt: string;
}

/**
 * Get status color from database data.
 * Resolution order: statusId -> status text (legacy) -> semanticType fallback.
 * @param status - Legacy status text value
 * @param statusData - Array of status data from database
 * @param statusId - Stable status ID
 * @param statusSemanticType - Canonical semantic bucket
 * @returns Hex color code or default gray if not found
 */
export const getStatusColor = (
  status: string | null | undefined,
  statusData: StatusData[],
  statusId?: string | null,
  statusSemanticType?: string | null,
): string => {
  if (statusId) {
    const byId = statusData.find((s) => s.id === statusId);
    if (byId?.color) return byId.color;
  }

  if (status) {
    const normalized = status.toLowerCase().trim();
    const byText = statusData.find((s) => {
      const name = (s.name || '').toLowerCase().trim();
      const displayName = (s.displayName || '').toLowerCase().trim();
      return name === normalized || displayName === normalized;
    });
    if (byText?.color) return byText.color;
  }

  switch ((statusSemanticType || '').toLowerCase().trim()) {
    case 'open':
      return '#ef4444';
    case 'in_progress':
      return '#47ef44';
    case 'completed':
      return '#1310b9';
    case 'deferred':
      return '#6b7280';
    default:
      return '#6b7280';
  }
};

/**
 * Get priority color from database data or by ID
 * @param priority - The priority name or ID to look up
 * @param priorityData - Array of priority data from database
 * @param priorityId - Optional priority ID to look up directly
 * @returns Hex color code
 */
export const getPriorityColor = (priority: string | null | undefined, priorityData?: PriorityData[], priorityId?: string | null): string => {
  // First try to find by priorityId if provided
  if (priorityId && priorityData) {
    const priorityItem = priorityData.find(p => p.id === priorityId);
    if (priorityItem?.color) {
      return priorityItem.color;
    }
  }
  
  // Fallback: try to find by name
  if (priority && priorityData) {
    const priorityItem = priorityData.find(p => p.name === priority);
    if (priorityItem?.color) {
      return priorityItem.color;
    }
  }
  
  // Fallback to hardcoded values for backward compatibility
  const priorityColors: Record<string, string> = {
    'Low': '#388E3C',        // Green
    'Medium': '#FBC02D',     // Yellow
    'High': '#F57C00',       // Orange
    'Critical': '#D32F2F',    // Red
    'Niedrig': '#388E3C',    // Green (old names)
    'Mittel': '#FBC02D',     // Yellow (old names)
    'Hoch': '#F57C00',       // Orange (old names)
    'Kritisch': '#D32F2F'    // Red (old names)
  };
  return priorityColors[priority || ''] || '#6b7280';
};

/**
 * Get status background color class for Tailwind CSS
 * @param status - The status name
 * @param statusData - Array of status data from database
 * @returns Tailwind CSS class string
 */
export const getStatusBgClass = (status: string, statusData: StatusData[]): string => {
  const color = getStatusColor(status, statusData);
  
  // Map hex colors to Tailwind classes
  const colorMap: Record<string, string> = {
    '#ef4444': 'bg-red-500',      // Open
    '#47ef44': 'bg-green-500',    // In progress (bright green)
    '#1310b9': 'bg-blue-600',     // Completed (dark blue)
    '#f1f65c': 'bg-yellow-400',   // Paused (yellow)
    '#6b7280': 'bg-gray-500'      // Default
  };
  
  return colorMap[color] || 'bg-gray-500';
};

/**
 * Get status text color class for Tailwind CSS
 * @param status - The status name
 * @param statusData - Array of status data from database
 * @returns Tailwind CSS class string
 */
export const getStatusTextClass = (status: string, statusData: StatusData[]): string => {
  const color = getStatusColor(status, statusData);
  
  // Map hex colors to Tailwind text classes
  const colorMap: Record<string, string> = {
    '#ef4444': 'text-red-700',      // Open
    '#47ef44': 'text-green-700',    // In progress
    '#1310b9': 'text-blue-700',     // Completed
    '#f1f65c': 'text-yellow-700',   // Paused
    '#6b7280': 'text-gray-700'      // Default
  };
  
  return colorMap[color] || 'text-gray-700';
};

/**
 * Get priority background color class for Tailwind CSS
 * @param priority - The priority name
 * @returns Tailwind CSS class string
 */
export const getPriorityBgClass = (priority: string): string => {
  const priorityClasses: Record<string, string> = {
    'Niedrig': 'bg-green-100',
    'Mittel': 'bg-yellow-100',
    'Hoch': 'bg-red-100',
    'Kritisch': 'bg-red-200'
  };
  return priorityClasses[priority] || 'bg-gray-100';
};

/**
 * Get priority text color class for Tailwind CSS
 * @param priority - The priority name
 * @returns Tailwind CSS class string
 */
export const getPriorityTextClass = (priority: string): string => {
  const priorityClasses: Record<string, string> = {
    'Niedrig': 'text-green-800',
    'Mittel': 'text-yellow-800',
    'Hoch': 'text-red-800',
    'Kritisch': 'text-red-900'
  };
  return priorityClasses[priority] || 'text-gray-800';
};

/**
 * Convert hex color to RGB values
 * @param hex - Hex color code (e.g., '#ef4444')
 * @returns RGB object or null if invalid
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Convert RGB values to hex color
 * @param r - Red value (0-255)
 * @param g - Green value (0-255)
 * @param b - Blue value (0-255)
 * @returns Hex color code
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};
